import { useRef } from "react";

import { useIsJsmCrossflow } from "@/components/signup/layouts/use-is-jsm-crossflow";
import { consumePersistedJsmCrossflow } from "@/components/signup/layouts/use-jsm-crossflow-persistence";

/**
 * Determines whether the current confirmation-page flow originated from a JSM
 * cross-flow or a direct Service Collection entry.
 *
 * On confirmation pages the URL no longer carries the `application` param or a
 * JSM referrer — only `primaryCollectionProduct=jira-servicedesk.ondemand` is
 * present for *both* JSM and ServCo flows, so `useIsJsmCrossflow` cannot
 * distinguish them on its own.
 *
 * Strategy (in priority order):
 *   1. sessionStorage value written by `JSMOverlayTemplate` on the preceding
 *      signup pages (most reliable — written while the URL still has the right
 *      signals).
 *   2. `useIsJsmCrossflow` URL-based detection as a fallback (covers the edge
 *      case where the user lands directly on the confirmation URL without
 *      going through the normal signup flow).
 *
 * Implementation notes:
 * - consumePersistedJsmCrossflow() deletes the sessionStorage key on first
 *   call (read-once semantics). A useRef caches the result so subsequent
 *   re-renders of the same component instance don't get null and silently
 *   fall back to URL-based detection.
 * - The confirmation-page screens (provisioning/welcome/team-focus) are
 *   mutually exclusive, so only one component instance consumes the key.
 */
export const useIsJsmCrossflowConfirmation = (): boolean => {
    const detectedFromUrl = useIsJsmCrossflow();
    const persistedRef = useRef<boolean | null | undefined>(undefined);
    if (persistedRef.current === undefined) {
        persistedRef.current = consumePersistedJsmCrossflow();
    }
    return persistedRef.current !== null
        ? persistedRef.current
        : detectedFromUrl;
};
